// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { GetShipTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import loadShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTos";
import { getShipTosDataView } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import constants from "@store/constants";
import DynamicDropdown, {
    DynamicDropdownPresentationProps,
    DynamicDropdownProps,
    OptionObject,
} from "@store/MobiusOverrides/DynamicDropdown/DynamicDropdown";
import debounce from "lodash/debounce";
import React, { FC, useCallback, useEffect, useState } from "react";

const getParameters = (filter?: string, additionalParameters?: GetShipTosApiParameter) => {
    const parameters: GetShipTosApiParameter = {
        billToId: API_URL_CURRENT_FRAGMENT,
        expand: ["validation"],
        exclude: ["createNew"],
        additionalExpands: ["includeCurrentTag"],
        ...additionalParameters,
    };

    if (filter) {
        parameters.filter = filter;
    }

    return parameters;
};

export interface SearchShipToFieldPresentationProps extends DynamicDropdownPresentationProps {}

export interface SearchShipToFieldProps extends Partial<DynamicDropdownProps> {
    additionalParameters?: Partial<GetShipTosApiParameter>;
    excludedCustomerSequences?: string[];
    onSelectionChangeShipTo?: (shipTo: ShipToModel | undefined) => void;
}

const SearchShipToField: FC<SearchShipToFieldProps> = ({
    additionalParameters,
    excludedCustomerSequences,
    onInputChange,
    onSelectionChange,
    onSelectionChangeShipTo,
    selected,
    ...otherDynamicDropdownProps
}) => {
    const dispatch = useAppDispatch();
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState(search);
    const debouncedSetFilter = useCallback(debounce(setFilter, constants.searchDebounceDelay), []);
    const canShowAll = !additionalParameters?.exclude?.includes("showAll");

    const { isLoading, value: shipTos } = useAppSelector(state =>
        getShipTosDataView(state, getParameters(filter, additionalParameters)),
    );

    useEffect(() => {
        if (!shipTos && !isLoading) {
            dispatch(loadShipTos(getParameters(filter, additionalParameters)));
        }
    }, [filter]);

    useEffect(() => {
        if (shipTos) {
            const options: OptionObject[] =
                shipTos
                    ?.filter(({ customerSequence }) => !excludedCustomerSequences?.includes(customerSequence))
                    .map(({ label, customerSequence }) => ({
                        optionText: label,
                        optionValue: customerSequence,
                    })) || [];

            setOptions(options);
        }
    }, [shipTos, excludedCustomerSequences]);

    return (
        <DynamicDropdown
            filterOption={() => true}
            isLoading={isLoading}
            options={options}
            search={search}
            selected={selected || (canShowAll ? constants.shipToSequences.showAll : undefined)}
            onSelectionChange={value => {
                const customerSequence = value === constants.shipToSequences.showAll ? "" : value;
                onSelectionChange?.(customerSequence);
                onSelectionChangeShipTo?.(shipTos?.find(x => x.customerSequence === customerSequence));
            }}
            onInputChange={event => {
                setSearch(event.currentTarget.value);
                debouncedSetFilter(event.currentTarget.value);
                onInputChange?.(event);
            }}
            {...otherDynamicDropdownProps}
        />
    );
};

export default SearchShipToField;
